// useLocalScheduler — Scheduler que roda DENTRO do Electron app.
//
// Por que existe: scheduled tasks rodam no Cloudflare Worker (apolo-api),
// que não consegue chamar Ollama local (localhost:11434 do Worker é o
// próprio datacenter, não a máquina do user). Tasks com adapter Ollama
// ou com flag `command_payload.execution_target === 'local'` ficam
// pendurada pra sempre se o Worker tentar — então:
//
// 1. Worker pula execução remota dessas tasks (vê `execution_target=local`)
// 2. Esse hook poll-a a lista de schedule, detecta tasks due, e executa
//    localmente via window.apolo.ollama.chat (+ URL/file prefetch)
// 3. Reporta resultado via POST /api/code-ide/schedule/:id/local-run
//
// A execução local em si (isTaskLocal / executeTaskLocally / reportRun) vive
// em lib/localTaskRun.js — compartilhada com o botão Play on-demand do card do
// agente (ChatWorkspaceSidebar), pra que o Play roteie modelos locais pelo MESMO
// caminho local (o cloud run-now responde "unknown model" pra modelo local).
//
// Tradeoff conhecido: só dispara enquanto Apolo tá aberto. Aceitamos.
//
// Polling de 30s é suficiente porque cron mínimo da UI é "a cada 6h" (e
// mesmo se for "a cada minuto", 30s de latência é aceitável).

import { useEffect, useRef } from 'react';
import { useLLMAdapters } from './useLLMAdapters.js';
import { useScheduledTasks } from './useScheduledTasks.js';
import { isTaskLocal, runLocalTaskNow, flushPendingLocalRuns } from '../lib/localTaskRun.js';

const POLL_INTERVAL_MS = 30_000;

function emitToast(detail) {
  window.dispatchEvent(new CustomEvent('apolo:toast', { detail }));
  window.dispatchEvent(new CustomEvent('apolo:scheduler:executed', { detail }));
}

export function useLocalScheduler() {
  const { load: reloadTasks } = useScheduledTasks();
  const { adapters } = useLLMAdapters({ kind: 'text' });
  const runningRef = useRef(new Set());

  // Mapa adapter_id → adapter
  const adaptersMapRef = useRef({});
  useEffect(() => {
    adaptersMapRef.current = Object.fromEntries((adapters || []).map((a) => [a.id, a]));
  }, [adapters]);

  useEffect(() => {
    let cancelled = false;
    let ticking = false;

    async function tick() {
      if (cancelled || ticking) return;
      ticking = true;
      try {
        await flushPendingLocalRuns();
        const now = Date.now();
        const currentTasks = await reloadTasks();
        if (!currentTasks || cancelled) return;
        const adaptersMap = adaptersMapRef.current;

        const dueLocal = currentTasks.filter((t) => {
          if (t.status !== 'enabled') return false;
          if (t.deleted_at) return false;
          if (!t.next_run_at || t.next_run_at > now) return false;
          if (runningRef.current.has(t.id)) return false;
          return isTaskLocal(t, adaptersMap);
        });

        for (const task of dueLocal) {
          if (cancelled) break;
          runningRef.current.add(task.id);
          emitToast({ kind: 'info', message: `▶ ${task.name} — executando local…` });
          try {
            const result = await runLocalTaskNow(task, adaptersMap, { scheduled: true });
            emitToast({ kind: result.status === 'ok' ? 'success' : 'error', message: result.status === 'ok' ? `${task.name} concluído` : `${task.name}: ${result.error}` });
          } catch (error) {
            emitToast({ kind: 'error', message: `${task.name}: ${error?.message}` });
          } finally {
            runningRef.current.delete(task.id);
          }
        }

      } catch (error) {
        emitToast({ kind: 'error', message: error?.message || 'Não foi possível sincronizar tarefas locais' });
      } finally { ticking = false; }
    }

    // Run once immediately + interval
    tick();
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [reloadTasks]);

  return null; // hook sem return de UI
}
